import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FileSignature, User, Briefcase, Calendar, PenTool, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { generateSignatureFormat, validateSignature } from "../../../utils/formValidators";

export default function Step5Declaration({ formData, setFormData, errors }) {
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [expectedSignature, setExpectedSignature] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "applicantName") {
      const format = generateSignatureFormat(value);
      setExpectedSignature(format);
      
      if (formData.applicantSignature) {
        const validation = validateSignature(formData.applicantSignature, value);
        setSignatureStatus(validation.isValid ? "valid" : "invalid");
      }
    }
  };

  const handleSignatureChange = (value) => {
    handleChange("applicantSignature", value);
    
    if (formData.applicantName && value) {
      const validation = validateSignature(value, formData.applicantName);
      setSignatureStatus(validation.isValid ? "valid" : "invalid");
    } else {
      setSignatureStatus(null);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!formData.declarationDate) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const todayDate = `${year}-${month}-${day}`;
      setFormData((prev) => ({
        ...prev,
        declarationDate: todayDate,
      }));
    }

    if (formData.applicantName) {
      setExpectedSignature(generateSignatureFormat(formData.applicantName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      {/* Section Title */}
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
          <FileSignature size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Declaration</h2>
      </div>

      {/* Form Fields - Two Columns */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Name of Applicant */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <User size={16} className="text-orange-600" />
              Name of Applicant <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.applicantName || ""}
              onChange={(e) => handleChange("applicantName", e.target.value)}
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.applicantName
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="Enter applicant name"
            />
            {errors?.applicantName && (
              <p className="mt-1 text-sm text-red-500">{errors.applicantName}</p>
            )}
          </div>

          {/* Designation of Signatory */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Briefcase size={16} className="text-orange-600" />
              Designation of Signatory <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.signatoryDesignation || ""}
              onChange={(e) => handleChange("signatoryDesignation", e.target.value)}
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.signatoryDesignation
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="Enter designation"
            />
            {errors?.signatoryDesignation && (
              <p className="mt-1 text-sm text-red-500">
                {errors.signatoryDesignation}
              </p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Signature of Applicant */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <PenTool size={16} className="text-orange-600" />
              Signature of Applicant <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.applicantSignature || ""}
                onChange={(e) => handleSignatureChange(e.target.value)}
                disabled={!formData.applicantName}
                className={`w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors?.applicantSignature
                    ? "border-red-300 bg-red-50"
                    : signatureStatus === "valid"
                    ? "border-green-300 bg-green-50"
                    : signatureStatus === "invalid"
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 hover:border-gray-400"
                } ${!formData.applicantName ? "opacity-50 cursor-not-allowed" : ""}`}
                placeholder={
                  formData.applicantName 
                    ? `Enter initials (e.g., ${expectedSignature})`
                    : "Enter applicant name first"
                }
              />
              {signatureStatus === "valid" && (
                <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />
              )}
              {signatureStatus === "invalid" && (
                <AlertCircle className="absolute top-3.5 right-3 text-red-500" size={20} />
              )}
            </div>
            
            {formData.applicantName && expectedSignature && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-2 items-start p-3 mt-2 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200"
              >
                <Info size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-blue-900">
                    Expected Signature Format
                  </p>
                  <p className="mt-1 text-sm font-bold text-blue-700">
                    {expectedSignature}
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    Based on: <span className="font-medium">{formData.applicantName}</span>
                  </p>
                </div>
              </motion.div>
            )}

            {!formData.applicantName && (
              <p className="mt-1.5 text-xs text-amber-600">
                Please enter the applicant name first to see the required signature format
              </p>
            )}

            {signatureStatus === "invalid" && expectedSignature && (
              <p className="flex gap-1 items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} />
                Signature must be: {expectedSignature}
              </p>
            )}

            {signatureStatus === "valid" && (
              <p className="flex gap-1 items-center mt-2 text-sm text-green-600">
                <CheckCircle2 size={14} />
                Signature format is correct!
              </p>
            )}

            {errors?.applicantSignature && (
              <p className="mt-1 text-sm text-red-500">
                {errors.applicantSignature}
              </p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Calendar size={16} className="text-orange-600" />
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.declarationDate || getTodayDate()}
              onChange={(e) => handleChange("declarationDate", e.target.value)}
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.declarationDate
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors?.declarationDate && (
              <p className="mt-1 text-sm text-red-500">{errors.declarationDate}</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

